import type {Tab, TabGroup, TabId} from '../types';
import {derived, type Readable, writable} from 'svelte/store';
import {createContext, getContextOrThrow} from 'src/core';
import {exists} from 'shared/utils/custom-utils';

const tabPagesMenuContextKey = Symbol('tab-pages-menu-ctx');

interface TabPagesMenuContext {
    menuId: string;
    tabGroups$: Readable<TabGroup[]>;
    selectedId$: Readable<TabId>;
    selectedTab$: Readable<Tab>;
    selectTab: (tabId: TabId) => void;
    updateTabGroups: (updater: (current: TabGroup[]) => TabGroup[]) => void;
}

export function createTabPagesMenuContext(menuId: string, tabGroups: TabGroup[], initiallySelectedTabId: TabId | null = null): TabPagesMenuContext {
    if (tabGroups.length === 0) {
        throw new Error('Tab pages menu must have at least one tab group');
    }

    const selectFirstTab = () => {
        const firstTabGroup = tabGroups[0];
        if (!exists(firstTabGroup.subtabs) || firstTabGroup.subtabs.length === 0) {
            return firstTabGroup.id;
        }

        return firstTabGroup.subtabs[0].id;
    };

    const firstTabId = initiallySelectedTabId ?? selectFirstTab();

    const tabGroups$ = writable<TabGroup[]>(tabGroups);
    const selectedId$ = writable<TabId>(firstTabId);
    const selectedTab$ = derived([selectedId$, tabGroups$], ([selectedId, groups]) => {
        return groups.flatMap((group) => group.subtabs ?? [group]).find((tab) => tab.id === selectedId);
    });

    return createContext<TabPagesMenuContext>(tabPagesMenuContextKey, {
        menuId,
        tabGroups$,
        selectedId$,
        selectedTab$,
        selectTab: (tabId) => selectedId$.set(tabId),
        updateTabGroups: (updater) => tabGroups$.update(updater)
    });
}

export function getTabPagesMenuContext(): TabPagesMenuContext {
    return getContextOrThrow<TabPagesMenuContext>(tabPagesMenuContextKey);
}